import { lazy, Suspense } from 'react';
import { Link, Route, Router as WouterRouter, Switch } from 'wouter';
import {
  ArrowRight,
  Clock3,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import {
  Header,
  MobileBar,
  SiteFooter,
  email,
  phone,
  phoneHref,
} from '@/components/site-layout';
import { additionalServiceOptions, serviceOptions } from '@/data/services';
import { loadQuotePage, preloadQuotePage } from '@/pages/quote-loader';
import LegalNoticePage from '@/pages/legal-notice';
import PrivacyPage from '@/pages/privacy';
import TermsPage from '@/pages/terms';

const queryClient = new QueryClient();

const QuotePage = lazy(loadQuotePage);
function HomePage() {
  return (
    <div className="site-shell noise">
          <Header />
          <main>
              <section className="hero">
              <div className="container-wide hero-grid">
                      <div>
                          <h1>Une maison soignée. <em>Du temps pour vous.</em></h1>
                          <p className="hero-copy">SHER CLEAN prend soin de votre intérieur avec rigueur et discrétion. Un service qui s’adapte à votre maison — et à votre rythme.</p>
                          <div className="hero-actions">
                              <Link href="/devis" className="btn-primary" onMouseEnter={preloadQuotePage} onFocus={preloadQuotePage} data-testid="hero-quote-link">Obtenir mon devis gratuit <ArrowRight size={17} /></Link>
                              <a href={phoneHref} className="btn-ghost" data-testid="hero-phone-link"><Phone size={16} /> {phone}</a>
                          </div>
                          <div className="hero-note"><ShieldCheck size={18} /> Sans engagement · Réponse personnalisée</div>
                      </div>
              </div>
          </section>

          <div className="trust-strip">
              <div className="container-wide trust-items">
                  <span className="trust-item"><ShieldCheck size={17} /> Une relation de confiance</span>
                  <span className="trust-item"><Clock3 size={17} /> Un rythme qui vous ressemble</span>
                  <span className="trust-item"><MapPin size={17} /> Troyes et alentours</span>
                  <span className="trust-item"><Sparkles size={17} /> Une entreprise locale, proche de vous</span>
              </div>
          </div>

          <section id="services" className="section">
              <div className="container-wide services-layout">
                  <div className="service-intro">
                      <span className="eyebrow">Ce que l’on peut faire pour vous</span>
                      <span className="gold-rule" style={{ marginTop: 18 }} />
                      <h2 className="font-display" style={{ fontSize: 'clamp(2.4rem, 4vw, 3.65rem)', lineHeight: 1.06, letterSpacing: '-.035em', margin: '16px 0' }}>Votre maison, vos besoins.</h2>
                      <p>Une aide ponctuelle ou une présence régulière : chaque intervention commence par l’écoute de vos habitudes et de votre intérieur.</p>
                      <Link href="/devis" className="btn-ghost" style={{ marginTop: 22 }} onMouseEnter={preloadQuotePage} onFocus={preloadQuotePage} data-testid="services-quote-link">Parler de mon besoin <ArrowRight size={16} /></Link>
                  </div>
                  <div className="service-grid">
                      {serviceOptions.map(({ label, detail, icon: Icon }, index) => (
                          <article className="service-card" key={label} data-testid={`card-service-${index}`}>
                              <Icon className="service-icon" size={27} strokeWidth={1.6} />
                              <div><h3>{label}</h3><p>{detail}</p></div>
                          </article>
                      ))}
                  </div>
              </div>
               <div className="container-wide additional-services-block">
                   <div className="additional-services-heading">
                       <span className="eyebrow">Autres services disponibles — sur devis</span>
                       <span className="gold-rule" style={{ marginTop: 18 }} />
                       <h2 className="font-display">Des services pensés autour de votre quotidien.</h2>
                       <p>
                         Ces services peuvent être demandés seuls ou associés à
                         une prestation de ménage ou de repassage. Chaque
                         demande fait l’objet d’un devis personnalisé.
                       </p>
                   </div>
                   <div className="additional-service-grid">
                       {additionalServiceOptions.map(({ label, detail, icon: Icon }, index) => (
                           <article className="additional-service-card" key={label} data-testid={`card-additional-service-${index}`}>
                               <div className="additional-service-card-top">
                                   <Icon className="service-icon" size={27} strokeWidth={1.6} />
                                   <span className="service-price">Sur devis</span>
                               </div>
                               <div>
                                   <h3>{label}</h3>
                                   <p>{detail}</p>
                               </div>
                           </article>
                       ))}
                   </div>
               </div>
          </section>

          <section id="methode" className="section method-section">
              <div className="container-wide">
                  <div className="section-heading">
                      <span className="eyebrow">Notre façon de faire</span>
                      <span className="gold-rule" style={{ marginTop: 18 }} />
                      <h2>Mise en place simple.<br />Une relation de confiance durable.</h2>
                      <p>Pas de formule imposée. On échange, on comprend votre intérieur, puis on prend le relais avec soin.</p>
                  </div>
                  <div className="steps">
                      <article className="step"><span className="step-number">01</span><h3>Vous nous parlez de votre maison</h3><p>Quelques informations sur votre logement, votre rythme et ce qui compte le plus pour vous.</p></article>
                      <article className="step"><span className="step-number">02</span><h3>Nous préparons un devis juste</h3><p>Un retour personnalisé, sans engagement, pour une prestation adaptée à la réalité de votre intérieur.</p></article>
                      <article className="step"><span className="step-number">03</span><h3>Vous retrouvez votre temps</h3><p>Un service attentif et discret, avec vos produits et votre matériel, dans le respect de vos habitudes.</p></article>
                  </div>
              </div>
          </section>

          <section id="tarif" className="section price-section">
              <div className="container-wide price-layout">
                  <div>
                      <span className="eyebrow">Un tarif lisible</span>
                      <span className="gold-rule" style={{ marginTop: 18 }} />
                      <h2 className="font-display" style={{ fontSize: 'clamp(2.4rem, 4.5vw, 4rem)', lineHeight: 1.04, letterSpacing: '-.04em', margin: '16px 0' }}>La clarté avant tout.</h2>
                      <p style={{ color: 'hsl(var(--muted-foreground))', lineHeight: 1.7, maxWidth: 470 }}>Pour le ménage régulier, le tarif est posé simplement. Le devis permet ensuite de tenir compte de votre logement et de vos besoins précis.</p>
                  </div>
                       <div className="price-card" data-testid="card-price">
                       <div className="price-label"><span>Offre de lancement · ménage régulier</span><strong className="price-number">28 € <small style={{ font: '500 .8rem var(--app-font-sans)' }}>TTC / heure</small></strong></div>
                       <p><strong>28 € TTC / heure le premier mois.</strong> À partir du deuxième mois, le tarif du ménage régulier est de <strong>32 € TTC / heure</strong>, avant éventuel avantage fiscal. Minimum 2 h par passage. Sur rendez-vous. Produits et matériel fournis par le client. Les éventuels frais supplémentaires, notamment de déplacement hors Troyes et alentours, de stationnement ou liés à une demande spécifique, sont annoncés dans le devis et validés avec vous avant la prestation.</p>
                  </div>
              </div>
              <div className="container-wide tax-benefits" data-testid="tax-benefits">
                  <h2 className="font-display tax-benefits-title">
                      Votre ménage peut ouvrir droit à un crédit d’impôt de 50 %
                  </h2>
                  <div className="tax-benefit-grid">
                      <article className="tax-benefit-card tax-benefit-card-primary">
                          <p>
                              Les prestations éligibles de ménage et de repassage à domicile peuvent vous permettre de bénéficier d’un crédit d’impôt égal à 50 % des dépenses restant effectivement à votre charge, sous réserve des conditions et plafonds légaux.
                          </p>
                          <p>
                              Par exemple, au tarif de 32 € TTC / heure appliqué à partir du deuxième mois, une heure revient à 16 € après crédit d’impôt. Au tarif de lancement de 28 € TTC / heure le premier mois, elle revient à 14 €, si vous remplissez les conditions et disposez du plafond nécessaire.
                          </p>
                          <p>
                              Une attestation fiscale annuelle vous est remise pour votre déclaration de revenus. Les aides reçues, notamment la participation de votre employeur au financement de CESU préfinancés, doivent être déduites des dépenses déclarées.
                          </p>
                      </article>
                      <article className="tax-benefit-card">
                          <h3>Avance immédiate — prochainement disponible</h3>
                          <p>
                              SHER CLEAN prépare la mise en place du service optionnel et gratuit d’Avance immédiate de l’Urssaf. Après activation et sous réserve de votre éligibilité, votre crédit d’impôt pourra être déduit directement lors du paiement des prestations éligibles.
                          </p>
                      </article>
                      <article className="tax-benefit-card">
                          <h3>CESU préfinancés — prochainement acceptés</h3>
                          <p>
                              L’acceptation des CESU préfinancés est en cours d’activation. Contactez-nous avant votre règlement pour connaître leur disponibilité et les modalités d’utilisation.
                          </p>
                      </article>
                  </div>
              </div>
          </section>

          <section className="quote-banner">
              <div className="container-wide">
                  <div className="quote-banner-inner">
                      <div><span className="eyebrow">Votre maison mérite de l’attention</span><h2>On commence par un échange, pas par une formule toute faite.</h2></div>
                      <Link href="/devis" className="btn-light" onMouseEnter={preloadQuotePage} onFocus={preloadQuotePage} data-testid="banner-quote-link">Demander mon devis <ArrowRight size={17} /></Link>
                  </div>
              </div>
          </section>

          <section id="contact" className="section contact-section">
              <div className="container-wide contact-layout">
                  <div className="contact-panel">
                      <span className="eyebrow" style={{ color: 'hsl(var(--accent))' }}>Une question ? Un premier échange ?</span>
                      <h2>Parlons de votre chez-vous.</h2>
                      <p>Vous préférez nous écrire directement ? Nous sommes à votre écoute pour comprendre votre besoin et vous orienter.</p>
                      <div className="contact-list">
                          <a href={phoneHref} className="contact-link" data-testid="contact-phone-link"><Phone size={18} /> {phone} <span style={{ opacity: .55, fontSize: '.72rem' }}>téléphone / WhatsApp</span></a>
                          <a href={`mailto:${email}`} className="contact-link" data-testid="contact-email-link"><Mail size={18} /> {email}</a>
                      </div>
                  </div>
                  <div className="address-panel">
                      <span className="eyebrow">SHER SOLUTIONS</span>
                      <h3>Une présence locale,<br />à taille humaine.</h3>
                      <p><MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 6, color: 'hsl(var(--secondary))' }} />6 Rue du Commandant Pierre Mulsant<br />10000 Troyes</p>
                      <p style={{ marginTop: 16, fontSize: '.73rem' }}>Semra Handan ER EI — SHER SOLUTIONS<br />SIREN 952 088 607</p>
                      <a href="https://sherclean.fr" target="_blank" rel="noreferrer" style={{ color: 'hsl(var(--primary))', fontWeight: 700, fontSize: '.82rem', marginTop: 15 }} data-testid="link-website">sherclean.fr <ArrowRight size={14} style={{ verticalAlign: 'middle' }} /></a>
                  </div>
              </div>
          </section>
      </main><SiteFooter /><MobileBar />
    </div>
  );
}
function Router() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<QuotePageFallback />}>
        <Switch>
          <Route path="/" component={HomePage} />
          <Route path="/devis" component={QuotePage} />
          <Route path="/mentions-legales" component={LegalNoticePage} />
          <Route
            path="/conditions-generales-de-vente"
            component={TermsPage}
          />
          <Route path="/confidentialite" component={PrivacyPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;

function QuotePageFallback() {
  return (
    <div className="site-shell noise">
      <Header />
      <main className="quote-page" aria-label="Chargement du formulaire de devis">
        <div className="container-wide quote-page-grid">
          <div className="form-shell" aria-busy="true">
            <div className="progress-top">
              <strong>Votre devis</strong>
              <span>Chargement…</span>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <MobileBar />
    </div>
  );
}
